import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { CheckCircle2, Compass, PlayCircle, RotateCcw } from "lucide-react";
import { useTours } from "./TourProvider";

// Header button listing the tours available to the user, to replay any of them.
function TourMenuButton() {
  const tours = useTours();
  const [open, setOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => !boxRef.current?.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!tours || tours.tours.length === 0) return null;

  const start = (id) => {
    setOpen(false);
    tours.startTour(id);
  };

  return (
    <div className="relative" ref={boxRef}>
      <motion.button
        type="button"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setOpen((v) => !v)}
        aria-label={tours.unseenCount ? `Feature tours, ${tours.unseenCount} new` : "Feature tours"}
        aria-expanded={open}
        title="Feature tours"
        data-tour="header-tours"
        className="relative w-10 h-10 flex items-center justify-center rounded-xl bg-violet-50 dark:bg-gray-800 border border-violet-100 dark:border-gray-700 shadow-sm hover:bg-violet-100 dark:hover:bg-gray-700 transition-colors"
      >
        <Compass className="w-5 h-5 text-violet-600 dark:text-violet-300" />
        {tours.unseenCount > 0 && (
          <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-violet-500 ring-2 ring-white dark:ring-gray-900" aria-hidden="true" />
        )}
      </motion.button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 rounded-xl border border-app-primary bg-app-card shadow-xl z-50 overflow-hidden" role="menu">
          <div className="px-4 py-3 border-b border-app-primary">
            <p className="text-sm font-semibold text-app-heading">Feature tours</p>
            <p className="text-xs text-app-muted">Short walkthroughs of what&apos;s new. Replay any time.</p>
          </div>
          <ul className="max-h-80 overflow-y-auto py-1">
            {tours.tours.map((tour) => {
              const done = tours.isDone(tour);
              return (
                <li key={tour.id}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => start(tour.id)}
                    className="w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-app-hover"
                  >
                    {done ? (
                      <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-green-500" aria-label="Seen" />
                    ) : (
                      <PlayCircle className="w-5 h-5 mt-0.5 shrink-0 text-violet-500" aria-label="New" />
                    )}
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 text-sm font-medium text-app-primary">
                        {tour.title}
                        {!done && <span className="rounded-full bg-violet-100 dark:bg-violet-500/20 px-1.5 text-[10px] font-semibold uppercase text-violet-700 dark:text-violet-300">New</span>}
                      </span>
                      <span className="block text-xs text-app-muted">{tour.description}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-app-primary px-4 py-2.5">
            <button
              type="button"
              disabled={resetting}
              onClick={async () => {
                setResetting(true);
                try {
                  await tours.resetTours();
                } finally {
                  setResetting(false);
                  setOpen(false);
                }
              }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-app-secondary hover:text-app-primary disabled:opacity-60"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Show all tours again automatically
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TourMenuButton;
